import { isoDateString } from '@clockwise/shared';
import { z } from 'zod';

/** A timesheet unlock as returned by the API, enriched with scope/grantor
 *  display names. Null `userId`/`customerId` are wildcards ("all"). */
export interface TimesheetUnlock {
  id: string;
  userId: string | null;
  userName: string | null;
  customerId: string | null;
  customerName: string | null;
  fromDate: string;
  toDate: string;
  autoEndDate: string;
  reason: string | null;
  createdBy: string | null;
  createdByName: string | null;
  revokedAt: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

const isoDate = isoDateString('Pick a date');

/** Body for POST /api/timesheet-unlocks. Mirrors the server's `createSchema`
 *  (apps/api/src/core/timesheet_unlocks/validators.ts). Cross-field rules
 *  (from <= to, auto-end not past) are enforced server-side and surfaced as
 *  field errors. `reason` is required — every unlock records why a period was
 *  re-opened. */
export const CreateTimesheetUnlockInput = z.object({
  userId: z.string().uuid().nullable().optional(),
  customerId: z.string().uuid().nullable().optional(),
  fromDate: isoDate,
  toDate: isoDate,
  autoEndDate: isoDate,
  reason: z.string().trim().min(1, 'A reason is required').max(1000),
});

export type CreateTimesheetUnlockBody = z.infer<typeof CreateTimesheetUnlockInput>;

/** Body for PUT /api/timesheet-unlocks/:id — the create payload plus an
 *  optional optimistic-concurrency token (`expectedUpdatedAt`, the ISO
 *  `updatedAt` the client last read). A stale token → 409. */
export type UpdateTimesheetUnlockBody = CreateTimesheetUnlockBody & {
  expectedUpdatedAt?: string;
};

export interface RevokeTimesheetUnlockBody {
  reason?: string | null;
}

export const UNLOCK_STATUS_FILTERS = ['active', 'inactive', 'all'] as const;
export type UnlockStatusFilter = (typeof UNLOCK_STATUS_FILTERS)[number];

export const UNLOCK_SORT_FIELDS = ['fromDate', 'autoEndDate', 'createdAt'] as const;
export type UnlockSortField = (typeof UNLOCK_SORT_FIELDS)[number];

/** Query params for the server-paginated management list. Mirrors the
 *  server's `listSchema` (apps/api/src/core/timesheet_unlocks/validators.ts). */
export interface ListTimesheetUnlocksQuery {
  status?: UnlockStatusFilter;
  sortField?: UnlockSortField;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ActiveForUserQuery {
  userId: string;
  fromDate: string;
  toDate: string;
}
